import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ScheduleConsultationDialog } from "@/components/ScheduleConsultationDialog";
import { ArrowLeft, TrendingUp, Users, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect } from "react";

const CaseStudyHealthtech = () => {
  useEffect(() => {
    document.title = "HealthTech Startup Case Study - Building Engaged Community | NQ Marketing";
  }, []);

  return (
    <div className="min-h-screen bg-background">
        <Navigation />
        
        <main className="container mx-auto px-4 pt-32 pb-20">
          <Link to="/#case-studies">
            <Button variant="ghost" className="mb-8">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Case Studies
            </Button>
          </Link>

          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <span className="inline-block px-4 py-2 rounded-full bg-secondary/10 text-secondary text-sm font-semibold mb-4">
                Social Media Marketing
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                HealthTech Startup Builds Engaged Community of 45K+ Followers
              </h1>
              <div className="flex flex-wrap gap-6 text-muted-foreground mb-8">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>8-month project</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  <span>HealthTech Industry</span>
                </div>
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  <span>12x Engagement Increase</span>
                </div>
              </div>
            </div>

            <div className="prose prose-lg max-w-none">
              <h2>The Challenge</h2>
              <p>
                A promising HealthTech startup had developed an innovative wellness app but struggled to build brand awareness and connect with their target audience. Their social media presence was minimal, with inconsistent posting and low engagement rates. They needed to establish credibility in a crowded market and build a community of health-conscious users.
              </p>

              <h2>Our Approach</h2>
              <p>
                We developed a comprehensive social media strategy centered on authentic storytelling and community building:
              </p>
              
              <h3>1. Platform Strategy & Optimization</h3>
              <ul>
                <li>Identified Instagram and LinkedIn as primary platforms based on audience research</li>
                <li>Redesigned social profiles with cohesive branding and clear value propositions</li>
                <li>Created content pillars: wellness tips, user success stories, expert insights, and behind-the-scenes</li>
                <li>Established consistent posting schedule with optimal timing analysis</li>
              </ul>

              <h3>2. Content Creation & Storytelling</h3>
              <ul>
                <li>Produced high-quality visual content including infographics and short-form videos</li>
                <li>Developed engaging carousel posts breaking down complex health topics</li>
                <li>Created user-generated content campaigns encouraging community participation</li>
                <li>Collaborated with micro-influencers in the wellness space</li>
              </ul>

              <h3>3. Community Engagement & Growth</h3>
              <ul>
                <li>Implemented proactive engagement strategy responding to comments within 2 hours</li>
                <li>Launched weekly live Q&A sessions with health experts</li>
                <li>Created exclusive community features and challenges</li>
                <li>Developed strategic partnership with complementary wellness brands</li>
              </ul>

              <h2>The Results</h2>
              <div className="grid md:grid-cols-3 gap-6 my-8 not-prose">
                <div className="bg-muted/10 rounded-2xl p-6 text-center">
                  <div className="text-4xl font-bold text-primary mb-2">45K+</div>
                  <div className="text-muted-foreground">New Followers</div>
                </div>
                <div className="bg-muted/10 rounded-2xl p-6 text-center">
                  <div className="text-4xl font-bold text-primary mb-2">12x</div>
                  <div className="text-muted-foreground">Engagement Rate Increase</div>
                </div>
                <div className="bg-muted/10 rounded-2xl p-6 text-center">
                  <div className="text-4xl font-bold text-primary mb-2">28%</div>
                  <div className="text-muted-foreground">App Install Rate from Social</div>
                </div>
              </div>

              <h2>Key Metrics Over 8 Months</h2>
              <ul>
                <li>Instagram followers grew from 3,200 to 38,500</li>
                <li>LinkedIn followers increased from 800 to 7,200</li>
                <li>Average post engagement rate improved from 1.2% to 14.8%</li>
                <li>Generated over 12,500 app installs directly attributed to social campaigns</li>
                <li>Built email list of 8,900 engaged subscribers through social channels</li>
              </ul>

              <h2>Key Takeaways</h2>
              <p>
                This project showcases the transformative power of authentic community building on social media. By focusing on providing genuine value and fostering meaningful connections, we helped the client establish themselves as a trusted voice in the wellness space.
              </p>
              <p>
                Critical success factors included:
              </p>
              <ul>
                <li>Consistency in brand voice and posting schedule</li>
                <li>Prioritizing engagement and conversation over follower count</li>
                <li>Leveraging user-generated content to build social proof</li>
                <li>Aligning content strategy with business goals (app installs)</li>
                <li>Investing in high-quality visual content that stands out</li>
              </ul>

              <div className="bg-primary/5 border-l-4 border-primary p-6 my-8">
                <p className="text-lg italic mb-2">
                  "Working with NQ Marketing was a game-changer for our brand. They didn't just grow our followers—they helped us build a real community of engaged users who believe in our mission. The results speak for themselves."
                </p>
                <p className="font-semibold">— CEO, HealthTech Startup</p>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t">
              <h3 className="text-2xl font-bold mb-4">Want to build your brand's community?</h3>
              <p className="text-muted-foreground mb-6">
                Let's create a social media strategy that drives real engagement and business results.
              </p>
              <ScheduleConsultationDialog
                buttonText="Get Started"
                buttonSize="lg"
              />
            </div>
          </div>
        </main>

        <Footer />
      </div>
  );
};

export default CaseStudyHealthtech;
